import React, { useState, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Card, Form, Input, Button, Typography, Divider, Space } from "antd";

import { toast } from 'react-toastify';
import { LockOutlined } from "@ant-design/icons";

const { Title, Text } = Typography;

const AcceptInvite = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [darkMode, setDarkMode] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  // Extract token from URL query parameters
  const queryParams = new URLSearchParams(location.search);
  const inviteToken = queryParams.get("token");

  const handleSubmit = async (values) => {
    try {
      setLoading(true);
      const response = await fetch(`${process.env.REACT_APP_API_URL}/InviteUser/Accept-Invite`,
  {
      method: "POST",
      headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      token: inviteToken,
      password: values.password
     }),
   }
);
      
  toast.success("Invitation accepted successfully!");
          navigate('/login');
    } catch (error) {
      console.log("Error", error);
      toast.error(error.response.data);
    } finally {
      setLoading(false);
    }
  };

  // Memoized theme styles
  const styles = useMemo(
    () => ({
      container: {
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        minHeight: "100vh",
        padding: "24px",
        backgroundColor: darkMode ? "#1f1f2e" : "#f8fafc",
        transition: "all 0.3s ease",
      },
      card: {
        width: "100%",
        maxWidth: "480px",
        borderRadius: "8px",
        boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
        border: "none",
        backgroundColor: darkMode ? "#2a2a3a" : "#ffffff",
      },
      header: {
        marginBottom: "16px",
        textAlign: "center",
      },
      formItem: {
        marginBottom: "20px",
      },
      submitButton: {
        backgroundColor: darkMode ? "#9A6B0Ec7" : "#9A6B0E",
        borderColor: darkMode ? "#9A6B0Ec7" : "#9A6B0E",
        color: "#fff",
        transition: "all 0.3s ease",
      },
      toggleContainer: {
        position: "absolute",
        top: "16px",
        right: "16px",
      },
      passwordHint: {
        color: darkMode ? "#a0a0a0" : "#666666",
        fontSize: "12px",
        marginTop: "-16px",
        marginBottom: "16px",
      },
    }),
    [darkMode]
  );

  return (
    <div style={styles.container}>
      <Card style={styles.card} bordered={false}>
        <Space direction="vertical" style={{ width: "100%" }}>
          <div style={styles.header}>
            <Title
              level={3}
              style={{ color: darkMode ? "#ffffff" : "#1a1a1a", marginBottom: "8px" }}
            >
              Accept Invitation
            </Title>
            <Text
              style={{ color: darkMode ? "#ffffff" : "rgb(26 26 26 / 38%)", marginBottom: "8px" }}
            >
              Set your password to complete the registration
            </Text>
          </div>

          <Divider style={{ margin: "16px 0", borderColor: darkMode ? "#3a3a4a" : "#e8e8e8" }} />

          <Form
            form={form}
            layout="vertical"
            onFinish={handleSubmit}
            initialValues={{ remember: true }}
          >
            <Form.Item
              name="password"
              label={<span style={{ color: darkMode ? "#fff" : "#000" }}>Password</span>}
              rules={[
                { required: true, message: "Please input your password" },
                { min: 8, message: "Password must be at least 8 characters" },
              ]}
              style={styles.formItem}
              hasFeedback
            >
              <Input.Password prefix={<LockOutlined />} placeholder="Enter a strong password" size="large" />
            </Form.Item>
            <Form.Item
  name="confirmPassword"
  label={<span style={{ color: darkMode ? "#fff" : "#000" }}>Confirm Password</span>}
  dependencies={['password']}
  hasFeedback
  rules={[
    { required: true, message: 'Please confirm your password!' },
    ({ getFieldValue }) => ({
      validator(_, value) {
        if (!value || getFieldValue('password') === value) {
          return Promise.resolve();
        }
        return Promise.reject(new Error('The two passwords do not match!'));
      },
    }),
  ]}
  style={styles.formItem}
>
  <Input.Password prefix={<LockOutlined />} placeholder="Confirm your password" size="large" />
</Form.Item>
            <br />

            <Form.Item>
              <Button
                type="primary"
                htmlType="submit"
                loading={loading}
                block
                style={styles.submitButton}
              >
                {loading ? "Processing..." : "Complete Registration"}
              </Button>
            </Form.Item>
          </Form>
        </Space>
      </Card>
    </div>
  );
};

export default AcceptInvite;
